import { useContext, useState, useRef, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import { ConfigContext } from '../../../contexts/ConfigContext';
import { reactSelectDark } from '../../../styles/reactSelectDarkTheme';
import { reactSelectLight } from '../../../styles/reactSelectLightTheme';
import FilterCalendar from './FilterCalendar';
import './FilterToolbar.css';

// Local YYYY-MM-DD. Deliberately NOT toISOString() — that converts to UTC and
// would shift the date back a day for anyone east of GMT (i.e. IST).
function toYmd(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-CA');
}

function fromYmd(value) {
  return value ? new Date(`${value}T00:00:00`) : null;
}

function labelFor(date) {
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Shared filter bar for server-paginated listings.
 *
 * Deliberately dumb — it owns no state. The parent holds the filter values so
 * they can be fed straight into the SWR hook (changing the URL key refetches).
 */

/**
 * Debounces a value so typing doesn't fire a request per keystroke.
 * Returned value is what should feed the data hook; the raw input stays local.
 */
export function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(typeof value === 'string' ? value.trim() : value), delay);
    return () => clearTimeout(timer.current);
  }, [value, delay]);

  return debounced;
}

export function useSelectTheme() {
  const config = useContext(ConfigContext);
  // ConfigContext exposes `mode` already resolved to 'dark' | 'light'.
  const base = config?.mode === 'dark' ? reactSelectDark : reactSelectLight;
  // The shared themes don't define menuPortal, so a portalled menu would render
  // behind the card without an explicit z-index.
  return { ...base, menuPortal: (styles) => ({ ...styles, zIndex: 9999 }) };
}

export function FilterSearch({ value, onChange, placeholder = 'Search...', width = 220 }) {
  return (
    <div className="ftb-search" style={{ width }}>
      <i className="ph ph-magnifying-glass ftb-search-icon" />
      <Form.Control
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="ftb-search-input"
      />
      {value && (
        <button type="button" className="ftb-search-clear" aria-label="Clear search" onClick={() => onChange('')}>
          <i className="ph ph-x" />
        </button>
      )}
    </div>
  );
}

export function FilterSelect({ options, value, onChange, width = 170, placeholder, isLoading }) {
  const theme = useSelectTheme();
  return (
    <div style={{ width }}>
      <Select
        options={options}
        value={options.find((o) => String(o.value) === String(value)) ?? options[0] ?? null}
        onChange={(opt) => onChange(opt?.value ?? options[0]?.value ?? '')}
        styles={theme}
        isSearchable={false}
        isLoading={isLoading}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </div>
  );
}

/**
 * Closes a popover on an outside click or Escape. Bound only while open so
 * we're not holding a document listener for every toolbar on the page.
 */
function useDismiss(open, setOpen, ref) {
  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen, ref]);
}

// The calendar's chrome, shared by every picker so they look the same
// (also used by components/DateRange in the Service Period forms).
export const CALENDAR_PROPS = {
  prev2Label: null,
  next2Label: null,
  prevLabel: <i className="ph ph-caret-left" />,
  nextLabel: <i className="ph ph-caret-right" />,
  // Two letters ("Mo", "Tu") — the three-letter default crowds the round days.
  formatShortWeekday: (locale, date) => date.toLocaleDateString(locale, { weekday: 'short' }).slice(0, 2)
};

/**
 * One control, one calendar. Click a day for a single date, click a second day
 * to extend it into a range — react-calendar's `selectRange` handles both, so
 * there's no separate "from"/"to" input to keep in sync.
 *
 * Emits { from, to } as YYYY-MM-DD. A single date emits the same value for
 * both, which the backends read as a one-day range.
 *
 * Optional: disabledDate(date) greys out days that can't be picked, and
 * openMonth (YYYY-MM-DD) is the month to open on while nothing is picked.
 */
export function FilterDateRange({ from, to, onChange, label = 'Date', width = 230, maxDays, disabledDate, openMonth }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useDismiss(open, setOpen, wrapRef);

  const start = fromYmd(from);
  const end = fromYmd(to);
  const calendarValue = start ? (end && end > start ? [start, end] : start) : null;

  // With maxDays, once a start day is picked the days too far from it to end
  // the range on are greyed out. Without it, nothing is disabled.
  const awaitingEnd = start && (!end || toYmd(end) === toYmd(start));
  const tileDisabled = ({ date, view }) =>
    Boolean(
      (disabledDate && view === 'month' && disabledDate(date)) ||
        (maxDays && awaitingEnd && view === 'month' && Math.abs(Math.round((date - start) / 86400000)) >= maxDays)
    );

  const handleChange = (next) => {
    const [first, second] = Array.isArray(next) ? next : [next, null];

    // First click: the start day. Applied straight away as a one-day range
    // (same value both ends, so the backends read it as that day alone), and
    // the calendar stays open for the end day.
    if (!second) {
      const ymd = toYmd(first);
      onChange({ from: ymd, to: ymd });
      return;
    }

    // Second click completes the range.
    onChange({ from: toYmd(first), to: toYmd(second) });
    setOpen(false);
  };

  const text = (() => {
    if (!start) return label;
    if (!end || toYmd(end) === toYmd(start)) return labelFor(start);
    return `${labelFor(start)} – ${labelFor(end)}`;
  })();

  // width="auto": the button is as wide as what it says — the placeholder,
  // then the whole "from – to" once dates are picked — rather than a fixed
  // width that cuts the range off.
  const fit = width === 'auto';

  return (
    <div className={`ftb-datepick${fit ? ' ftb-datepick--fit' : ''}`} ref={wrapRef} style={fit ? undefined : { width }}>
      <button
        type="button"
        className={`ftb-datepick-btn${start ? ' ftb-datepick-btn--set' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className="ph ph-calendar-blank ftb-datepick-icon" />
        <span className="ftb-datepick-text">{text}</span>
        {start ? (
          <i
            className="ph ph-x ftb-datepick-clear"
            role="button"
            aria-label="Clear date"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ from: '', to: '' });
              setOpen(false);
            }}
          />
        ) : (
          <i className="ph ph-caret-down ftb-datepick-caret" />
        )}
      </button>

      {open && (
        <div className="ftb-datepick-pop" role="dialog" aria-label={`${label} picker`}>
          <FilterCalendar
            selectRange
            onChange={handleChange}
            value={calendarValue}
            tileDisabled={tileDisabled}
            openMonth={fromYmd(openMonth) || undefined}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The single-day version of FilterDateRange: same button, same calendar.
 *
 * Takes and emits one YYYY-MM-DD string ('' when cleared), so it drops in
 * where a native <input type="date"> was — whose pop-up the browser draws and
 * no theme can reach. Takes the same optional disabledDate and openMonth as
 * FilterDateRange.
 */
export function FilterDate({ value, onChange, placeholder = 'Select date', width = 180, min, disabledDate, openMonth }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useDismiss(open, setOpen, wrapRef);

  const date = fromYmd(value);
  // Optional earliest day (YYYY-MM-DD), as the native input's `min`.
  const minDate = fromYmd(min) || undefined;

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="ftb-datepick" ref={wrapRef} style={{ width }}>
      <button
        type="button"
        className={`ftb-datepick-btn${date ? ' ftb-datepick-btn--set' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className="ph ph-calendar-blank ftb-datepick-icon" />
        <span className="ftb-datepick-text">{date ? labelFor(date) : placeholder}</span>
        {date ? (
          <i
            className="ph ph-x ftb-datepick-clear"
            role="button"
            aria-label="Clear date"
            onClick={(e) => {
              e.stopPropagation();
              pick('');
            }}
          />
        ) : (
          <i className="ph ph-caret-down ftb-datepick-caret" />
        )}
      </button>

      {open && (
        <div className="ftb-datepick-pop" role="dialog" aria-label="Date picker">
          <FilterCalendar
            onChange={(d) => pick(toYmd(d))}
            value={date}
            minDate={minDate}
            tileDisabled={disabledDate ? ({ date: d }) => disabledDate(d) : undefined}
            openMonth={fromYmd(openMonth) || undefined}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Re-exported so toolbars pull everything from one place. It's the same
 * icon-only control Custom Setup and Booking Management already use — kept
 * shared rather than forked so all four screens stay identical.
 */
export { default as FilterReset } from '../ClearFiltersButton/ClearFiltersButton';

/** `inline` drops the bottom margin for use inside an existing header row. */
export default function FilterToolbar({ children, inline = false }) {
  return <div className={`ftb${inline ? ' ftb--inline' : ''}`}>{children}</div>;
}
